import { useState, useEffect } from 'react';
import { Avatar } from '../../../components/shared/UIComponents';
import { useSchool } from '../../../contexts/SchoolContext';
import { useRoleSwitcher } from '../../auth/hooks/useRoleSwitcher.jsx';
import { flagTypesService } from '../../../services/flagTypesService.js';
import StudentFlagChip from './StudentFlagChip.jsx';
import StudentFlagsEditor from './StudentFlagsEditor.jsx';
import StudentMedicalAlert from './StudentMedicalAlert.jsx';
import useHealthProfile from '../../health/hooks/useHealthProfile.js';
import { getSeriousMedicalItems } from '../../health/services/healthProfileService.js';

const StudentHeader = ({
  student,
  onBack,
  compact = false,
  showBack = true,
  allowFlagEditing = true,
}) => {
  const { schoolId } = useSchool();
  const { currentRole } = useRoleSwitcher();

  const { profile: healthProfile } = useHealthProfile({ schoolId, studentId: student.id });
  const medicalAlerts = getSeriousMedicalItems(healthProfile);

  const [allFlagTypes, setAllFlagTypes] = useState([]);
  const [flagEditorOpen, setFlagEditorOpen] = useState(false);

  useEffect(() => {
    if (!schoolId) return;
    flagTypesService.getAll(schoolId).then(setAllFlagTypes).catch(console.error);
  }, [schoolId]);

  // Resolve student.flags (array of IDs) → FlagType objects
  const studentFlagIds = student.flags || [];
  const flagTypeMap = Object.fromEntries(allFlagTypes.map(ft => [ft.id, ft]));
  const resolvedFlags = studentFlagIds
    .map(id => flagTypeMap[id])
    .filter(Boolean);

  // canEditFlags: admin, teacher, or dev
  const canEditFlags = allowFlagEditing && (
    currentRole === 'admin' ||
    currentRole === 'teacher' ||
    currentRole === 'dev'
  );

  return (
    <div className={`card bg-base-100 shadow-sm rounded-2xl ${compact ? 'p-4' : 'p-6 mb-6'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Avatar
            firstName={student.firstName}
            surname={student.surname}
            photoURL={student.photoURL}
            size={compact ? 'md' : 'lg'}
          />
          <div>
            <h1 className={`${compact ? 'text-lg' : 'text-2xl'} font-bold leading-tight`}>
              {student.firstName} {student.surname}
            </h1>
            <p className={`${compact ? 'text-xs' : ''} text-base-content/60`}>
              {student.grade ? `Grade ${student.grade} · ` : ''}{student.className} • {student.studentNumber || 'No ID'}
            </p>

            {/* Flag chips row */}
            {resolvedFlags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {resolvedFlags.map(ft => (
                  <StudentFlagChip key={ft.id} flagType={ft} />
                ))}
              </div>
            )}

            {/* Edit flags control — admin + teacher + dev only */}
            {canEditFlags && (
              <button
                type="button"
                onClick={() => setFlagEditorOpen(true)}
                className="btn btn-ghost btn-xs mt-1"
              >
                Edit flags
              </button>
            )}
          </div>
        </div>
        {showBack && (
          <button
            onClick={onBack}
            className="btn btn-ghost btn-sm"
          >
            Back
          </button>
        )}
      </div>

      {/* Serious medical alert — full-width row at the foot of the header so it's
          un-missable on every tab without disturbing the identity block above */}
      {medicalAlerts.length > 0 && (
        <div className="mt-4">
          <StudentMedicalAlert items={medicalAlerts} />
        </div>
      )}

      {flagEditorOpen && (
        <StudentFlagsEditor
          student={student}
          schoolId={schoolId}
          onClose={() => setFlagEditorOpen(false)}
        />
      )}
    </div>
  );
};

export default StudentHeader;
